/* Copyright © 2026 YOUR GROWTH 365 (Jonas Brössel) — Proprietary. See LICENSE in repo root. */
/* =============================================================================
   VIEWS / ADMIN / STATS-TAGESGESCHÄFT — Bereich A (S180 Auswertung-Rework, Cut 2)
   =============================================================================
   Füllt die Tagesgeschäft-Shell mit echten Kennzahlen. Daten frisch über die
   Lese-RPCs (phase_135), NIE aus _state-Cache. Charts = inline-SVG/HTML (kein
   recharts/Chart.js), Dark-Mode über CSS-Tokens (var(--green) etc.).

   Aufbau (Handoff 2a–2d):
     2a stats_revenue_overview → KPI-Karten (Umsatz/Ø-Bon/Gäste/Bestellungen + Δ
        Vorzeitraum), Umsatz-Linie (SVG), Kanal-Split, Steuer-Split.
     2b stats_peak_hours       → Stoßzeiten-Heatmap (Stunde × Wochentag).
     2c stats_item_ranking     → Speisen/Getränke-Ranking.
     2d stats_discounts_returns→ Rabatt-Report + Storno-Quote.

   Pure Aggregat-/Chart-Helfer sind als _-Exports für Vitest exponiert.
============================================================================ */

import { htmlEscape } from '../../utils/helpers.js';
import { collapsibleRankHtml } from './stats-collapse.js';

/* -------------------------------------------------------------------------- */
/* Pure Helfer (Vitest)                                                        */
/* -------------------------------------------------------------------------- */

/** Δ in % zwischen cur und prev. null wenn prev<=0 (kein sinnvoller Bezug). */
export function deltaPct(cur, prev) {
  const c = Number(cur) || 0, p = Number(prev) || 0;
  if (p <= 0) return null;
  return Math.round(((c - p) / p) * 1000) / 10;   // 1 Nachkommastelle
}

/** Cent → „12,34 €". */
export function fmtCents(cents) {
  return (((Number(cents) || 0) / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })) + ' €';
}

const CHANNEL_LABEL = { 'dine-in': 'Hier Essen', takeaway: 'Takeaway', delivery: 'Lieferung' };
export function channelLabel(svc) { return CHANNEL_LABEL[svc] || svc || '—'; }

/** Kanal-Rohdaten → [{svc,label,gross,pct}] absteigend, pct vom Gesamt. */
export function buildChannelRows(channels) {
  const list = Array.isArray(channels) ? channels : [];
  const total = list.reduce((s, c) => s + (Number(c.gross_cents) || 0), 0);
  return list
    .map(c => ({
      svc: c.service_type,
      label: channelLabel(c.service_type),
      gross: Number(c.gross_cents) || 0,
      pct: total > 0 ? Math.round((Number(c.gross_cents) || 0) / total * 100) : 0
    }))
    .sort((a, b) => b.gross - a.gross);
}

/**
 * Sparkline-Pfad aus einer Werte-Reihe. Liefert { line, area, points, max }.
 * Pure-Geometrie (für Vitest). Bei 0/1 Punkt defensive Sonderfälle.
 */
export function sparklinePath(values, w = 600, h = 120, pad = 8) {
  const vals = (Array.isArray(values) ? values : []).map(v => Number(v) || 0);
  const max = Math.max(0, ...vals);
  const innerW = w - 2 * pad, innerH = h - 2 * pad;
  if (vals.length === 0) return { line: '', area: '', points: [], max: 0 };
  const stepX = vals.length > 1 ? innerW / (vals.length - 1) : 0;
  const points = vals.map((v, i) => {
    const x = pad + (vals.length > 1 ? i * stepX : innerW / 2);
    const y = max > 0 ? (pad + innerH - (v / max) * innerH) : (pad + innerH);
    return { x: Math.round(x * 100) / 100, y: Math.round(y * 100) / 100 };
  });
  const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x} ${p.y}`).join(' ');
  const area = points.length
    ? `M${points[0].x} ${h - pad} ` + points.map(p => `L${p.x} ${p.y}`).join(' ') + ` L${points[points.length - 1].x} ${h - pad} Z`
    : '';
  return { line, area, points, max };
}

/** Peak-Cells → {grid: {dow→{hour→{gross,cnt}}}, max, hours:[min..max]}. */
export function peakMatrix(cells) {
  const list = Array.isArray(cells) ? cells : [];
  const grid = {};
  let max = 0, hMin = 23, hMax = 11;
  for (const c of list) {
    const dow = Number(c.dow), hr = Number(c.hour), g = Number(c.gross_cents) || 0;
    if (!grid[dow]) grid[dow] = {};
    grid[dow][hr] = { gross: g, cnt: Number(c.bon_count) || 0 };
    if (g > max) max = g;
    if (hr < hMin) hMin = hr;
    if (hr > hMax) hMax = hr;
  }
  if (hMin > hMax) { hMin = 11; hMax = 22; }   // leer → Standardfenster
  const hours = [];
  for (let h = hMin; h <= hMax; h++) hours.push(h);
  return { grid, max, hours };
}

/** Ranking-Items → {food:[…], drink:[…]} (qty-absteigend, schon vom Server sortiert). */
export function splitRanking(items) {
  const list = Array.isArray(items) ? items : [];
  return {
    food:  list.filter(i => i.kind === 'food'),
    drink: list.filter(i => i.kind === 'drink')
  };
}

/** Deal-Anteil am Umsatz in % (0 wenn kein Bezugsumsatz). */
export function dealSharePct(dealRevenue, salesGross) {
  const s = Number(salesGross) || 0;
  if (s <= 0) return 0;
  return Math.round((Number(dealRevenue) || 0) / s * 1000) / 10;   // 1 Nachkommastelle
}

/** Deal-Rohdaten → [{deal_id,name,count,revenue,pct}] absteigend nach Umsatz. */
export function buildDealRows(deals, salesGross) {
  const list = Array.isArray(deals) ? deals : [];
  return list
    .map(d => ({
      deal_id: d.deal_id,
      name: d.name || d.deal_id || '—',
      count: Number(d.count) || 0,
      revenue: Number(d.revenue_cents) || 0,
      pct: dealSharePct(Number(d.revenue_cents) || 0, salesGross)
    }))
    .sort((a, b) => b.revenue - a.revenue);
}

/* -------------------------------------------------------------------------- */
/* Render-Helfer (inline-SVG/HTML, Dark-Mode-Token-Farben)                     */
/* -------------------------------------------------------------------------- */

function _deltaBadge(d) {
  if (d == null) return `<span class="stats-delta stats-delta-flat" title="kein Vorzeitraum">–</span>`;
  const up = d >= 0;
  const cls = up ? 'stats-delta-up' : 'stats-delta-down';
  return `<span class="stats-delta ${cls}">${up ? '▲' : '▼'} ${Math.abs(d).toLocaleString('de-DE')} %</span>`;
}

function _kpiCard(label, value, sub, delta) {
  return `
    <div class="stat">
      <div class="stat-label">${htmlEscape(label)}</div>
      <div class="stat-value">${value}</div>
      <div class="stat-sub">${sub || '&nbsp;'} ${delta !== undefined ? _deltaBadge(delta) : ''}</div>
    </div>`;
}

function _revenueLineSvg(daily) {
  const vals = (daily || []).map(d => Number(d.gross_cents) || 0);
  if (!vals.length) return `<p class="hint" style="text-align:center;padding:18px;">Keine Umsätze im Zeitraum.</p>`;
  const W = 600, H = 130;
  const { line, area, points } = sparklinePath(vals, W, H, 10);
  const dots = points.map((p, i) =>
    `<circle cx="${p.x}" cy="${p.y}" r="2.5" fill="var(--green)"><title>${htmlEscape((daily[i] && daily[i].day) || '')}: ${fmtCents(vals[i])}</title></circle>`
  ).join('');
  return `
    <svg viewBox="0 0 ${W} ${H}" preserveAspectRatio="none" style="width:100%;height:130px;display:block;">
      <path d="${area}" fill="var(--green)" opacity="0.12"></path>
      <path d="${line}" fill="none" stroke="var(--green)" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"></path>
      ${dots}
    </svg>`;
}

function _channelBars(channels) {
  const rows = buildChannelRows(channels);
  if (!rows.length) return `<p class="hint">Keine Daten.</p>`;
  return rows.map(r => `
    <div class="stats-channel-row" style="display:flex;align-items:center;gap:10px;margin:6px 0;">
      <span style="min-width:90px;">${htmlEscape(r.label)}</span>
      <span class="stats-bar-track" style="flex:1;height:14px;background:var(--bg-soft);border-radius:7px;overflow:hidden;">
        <span style="display:block;height:100%;width:${r.pct}%;background:var(--green);"></span>
      </span>
      <span style="min-width:120px;text-align:right;">${fmtCents(r.gross)} · ${r.pct}%</span>
    </div>`).join('');
}

function _peakHeatmap(cells) {
  const { grid, max, hours } = peakMatrix(cells);
  if (!max) return `<p class="hint">Keine Belege im Zeitraum.</p>`;
  const dows = [1, 2, 3, 4, 5, 6, 7];
  const dowLabel = { 1: 'Mo', 2: 'Di', 3: 'Mi', 4: 'Do', 5: 'Fr', 6: 'Sa', 7: 'So' };
  const head = `<div class="hm2-row"><div class="hm2-corner"></div>${hours.map(h => `<div class="hm2-h">${h}</div>`).join('')}</div>`;
  const body = dows.map(dw => {
    const cellsHtml = hours.map(h => {
      const cell = grid[dw] && grid[dw][h];
      const g = cell ? cell.gross : 0;
      const op = max > 0 ? (0.12 + 0.88 * (g / max)) : 0;
      const bg = g > 0 ? `background:var(--green);opacity:${Math.round(op * 100) / 100};` : 'background:var(--bg-soft);';
      const title = `${dowLabel[dw]} ${h}–${h + 1} Uhr: ${fmtCents(g)}${cell ? ' · ' + cell.cnt + ' Belege' : ''}`;
      return `<div class="hm2-cell" style="${bg}" title="${htmlEscape(title)}"></div>`;
    }).join('');
    return `<div class="hm2-row"><div class="hm2-d">${dowLabel[dw]}</div>${cellsHtml}</div>`;
  }).join('');
  return `<div class="hm2">${head}${body}</div>`;
}

function _rankingList(items) {
  const { food, drink } = splitRanking(items);
  // S217 Cut 3 — ganze Rangliste rechnen (Server cappt nicht), Anzeige Top 5 +
  // „alle anzeigen" aufklappbar (gemeinsames Grundmuster, collapsibleRankHtml).
  const col = (title, list, key) => {
    const li = list.map((i, idx) => `<li style="margin:5px 0;display:flex;align-items:baseline;gap:10px;">
          <span style="flex:0 0 18px;color:var(--ink-muted);font-variant-numeric:tabular-nums;">${idx + 1}.</span>
          <span style="flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${htmlEscape(i.name)}</span>
          <span style="flex:0 0 auto;white-space:nowrap;color:var(--ink-soft);font-variant-numeric:tabular-nums;">${i.qty}× · ${fmtCents(i.revenue_cents)}</span>
        </li>`).join('');
    return `
    <div class="card">
      <h4 style="margin:0 0 10px;">${title}</h4>
      ${list.length ? collapsibleRankHtml(key, li, list.length) : `<p class="hint">Keine Verkäufe.</p>`}
    </div>`;
  };
  return `<div class="stats-duo">${col('🍔 Speisen (7 %)', food, 'tg-food')}${col('🥤 Getränke (19 %)', drink, 'tg-drink')}</div>`;
}

function _dealsSection(dealData, salesGross) {
  const rows = buildDealRows(dealData && dealData.deals, salesGross);
  const total = Number(dealData && dealData.deals_total) || 0;
  const rev = Number(dealData && dealData.deal_revenue_cents) || 0;
  const share = dealSharePct(rev, salesGross);
  // S217 Cut 3 — Top 5 + „alle anzeigen" (gemeinsames Grundmuster).
  const dealLi = rows.map((r, idx) => `<li style="margin:5px 0;display:flex;align-items:baseline;gap:10px;">
          <span style="flex:0 0 18px;color:var(--ink-muted);font-variant-numeric:tabular-nums;">${idx + 1}.</span>
          <span style="flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${htmlEscape(r.name)}</span>
          <span style="flex:0 0 auto;white-space:nowrap;color:var(--ink-soft);font-variant-numeric:tabular-nums;">${r.count}× · ${fmtCents(r.revenue)}</span>
        </li>`).join('');
  const list = rows.length
    ? collapsibleRankHtml('tg-deals', dealLi, rows.length)
    : `<p class="hint">Keine Deals im Zeitraum verkauft.</p>`;
  return `
    ${rows.length ? `<div class="stat-sub" style="margin:0 0 10px;"><strong>${total}</strong> Deal${total === 1 ? '' : 's'} verkauft · <strong>${fmtCents(rev)}</strong> Umsatz · ${share.toLocaleString('de-DE')} % vom Umsatz</div>` : ''}
    ${list}`;
}

function _discountsReturns(d) {
  const discs = Array.isArray(d.discounts) ? d.discounts : [];
  const discRows = discs.length
    ? discs.map(x => `<li style="margin:5px 0;display:flex;align-items:baseline;gap:10px;"><span style="flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${htmlEscape(x.discount_name)}</span><span style="flex:0 0 auto;white-space:nowrap;color:var(--ink-soft);font-variant-numeric:tabular-nums;">${x.count}× · ${fmtCents(x.cents)}</span></li>`).join('')
    : `<li class="hint">Keine Rabatte im Zeitraum.</li>`;
  return `
    <div class="stats-duo">
      <div class="card">
        <h4 style="margin:0 0 8px;">Rabatte</h4>
        <ol class="stats-rank" style="margin:0;padding:0;list-style:none;">${discRows}</ol>
        <div class="stat-sub" style="margin-top:8px;">Summe: <strong>${fmtCents(d.discount_total_cents)}</strong></div>
      </div>
      <div class="card">
        <h4 style="margin:0 0 8px;">Storno</h4>
        <div class="stat-value">${(Number(d.storno_quote_pct) || 0).toLocaleString('de-DE')} %</div>
        <div class="stat-sub">${d.storno_count || 0} Stornobelege · ${fmtCents(d.storno_cents)} von ${fmtCents(d.sales_gross_cents)} Umsatz</div>
      </div>
    </div>`;
}

/* -------------------------------------------------------------------------- */
/* Daten laden (RPCs) + Render                                                 */
/* -------------------------------------------------------------------------- */

function _supa() { return (window.App && window.App.supabase) || null; }

async function _rpc(name, from, to) {
  const supa = _supa();
  if (!supa) throw new Error('Keine Verbindung.');
  const { data, error } = await supa.rpc(name, { p_from: from, p_to: to });
  if (error) throw error;
  return data || {};
}

/** Vorzeitraum gleicher Länge unmittelbar davor (gleich langes Fenster). */
function _prevRange(from, to) {
  const f = new Date(from + 'T12:00:00'), t = new Date(to + 'T12:00:00');
  const days = Math.round((t - f) / 86400000) + 1;
  const pf = new Date(f); pf.setDate(pf.getDate() - days);
  const pt = new Date(f); pt.setDate(pt.getDate() - 1);
  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { from: iso(pf), to: iso(pt) };
}

/**
 * Lädt + rendert das Tagesgeschäft-Dashboard in `el`. range = {from,to}.
 * Non-destruktiv aufrufbar (ersetzt nur den Inhalt von `el`).
 */
export async function renderTagesgeschaeftInto(el, range) {
  if (!el) return;
  if (!range || !range.from || !range.to) {
    el.innerHTML = `<p class="hint" style="text-align:center;padding:24px;">Zeitraum wählen.</p>`;
    return;
  }
  el.innerHTML = `<p class="hint" style="text-align:center;padding:24px;">Lade Kennzahlen…</p>`;
  const { from, to } = range;
  const prev = _prevRange(from, to);
  // allSettled (nicht all): EIN fehlschlagender RPC (z. B. noch nicht applizierte
  // Migration) degradiert nur seine eigene Sektion, nicht das ganze Dashboard.
  const calls = [
    ['stats_revenue_overview', from, to],
    ['stats_revenue_overview', prev.from, prev.to],
    ['stats_peak_hours', from, to],
    ['stats_item_ranking', from, to],
    ['stats_discounts_returns', from, to],
    ['stats_deal_sales', from, to]
  ];
  try {
    const settled = await Promise.allSettled(calls.map(([n, f, t]) => _rpc(n, f, t)));
    settled.forEach((s, i) => {
      if (s.status === 'rejected') console.error('[stats-tg] rpc', calls[i][0], s.reason);
    });
    const val = (i) => (settled[i].status === 'fulfilled' ? settled[i].value : null);
    el.innerHTML = _renderDashboard(val(0), val(1) || {}, val(2), val(3), val(4), val(5));
  } catch (e) {
    console.error('[stats-tg] load:', e);
    el.innerHTML = `<p class="hint" style="text-align:center;padding:24px;">Kennzahlen konnten nicht geladen werden: ${htmlEscape(e.message || String(e))}</p>`;
  }
}

/** Degradierter Sektions-Platzhalter, wenn ein einzelner RPC fehlschlug. */
function _sectionError(label) {
  return `<p class="hint" style="text-align:center;padding:18px;">${htmlEscape(label)} derzeit nicht verfügbar.</p>`;
}

function _renderDashboard(rev, revPrev, peak, rank, disc, deals) {
  const rp = revPrev || {};
  // rev speist KPIs/Umsatzlinie/Kanäle/Deal-Anteil — bei Ausfall diese Sektionen degradieren.
  const kpis = rev ? `
    <div class="stats">
      ${_kpiCard('Umsatz (brutto)', fmtCents(rev.gross_cents), 'netto ' + fmtCents(rev.net_cents), deltaPct(rev.gross_cents, rp.gross_cents))}
      ${_kpiCard('Ø-Bon', fmtCents(rev.avg_bon_cents), `${rev.bon_count || 0} Belege`, deltaPct(rev.avg_bon_cents, rp.avg_bon_cents))}
      ${_kpiCard('Gäste', (rev.guests || 0).toLocaleString('de-DE'), 'Hier Essen', deltaPct(rev.guests, rp.guests))}
      ${_kpiCard('Bestellungen', (rev.bon_count || 0).toLocaleString('de-DE'), 'Belege', deltaPct(rev.bon_count, rp.bon_count))}
    </div>` : _sectionError('Umsatz-Kennzahlen');
  return `
    ${kpis}
    <div class="card" style="margin-top:16px;">
      <h3 style="margin:0 0 8px;">Umsatz pro Tag</h3>
      ${rev ? _revenueLineSvg(rev.daily) : _sectionError('Umsatzverlauf')}
    </div>
    <div class="stats-duo" style="margin-top:16px;">
      <div class="card">
        <h3 style="margin:0 0 8px;">Kanäle</h3>
        ${rev ? `${_channelBars(rev.channels)}
        <div class="stat-sub" style="margin-top:10px;">USt 7 %: ${fmtCents(rev.tax7_cents)} · USt 19 %: ${fmtCents(rev.tax19_cents)}</div>` : _sectionError('Kanäle')}
      </div>
      <div class="card">
        <h3 style="margin:0 0 8px;">Stoßzeiten</h3>
        ${peak ? _peakHeatmap(peak.cells) : _sectionError('Stoßzeiten')}
      </div>
    </div>
    <div class="card" style="margin-top:16px;">
      <h3 style="margin:0 0 10px;">Speisen- & Getränke-Ranking</h3>
      ${rank ? _rankingList(rank.items) : _sectionError('Ranking')}
    </div>
    <div class="card" style="margin-top:16px;">
      <h3 style="margin:0 0 10px;">Deals</h3>
      ${deals ? _dealsSection(deals, rev ? rev.sales_gross_cents : 0) : _sectionError('Deals')}
    </div>
    <div class="card" style="margin-top:16px;">
      <h3 style="margin:0 0 10px;">Rabatte & Storno</h3>
      ${disc ? _discountsReturns(disc) : _sectionError('Rabatte & Storno')}
    </div>`;
}
